/**
 * useScopedRuns — module-agnostic scope filter for module runs lists.
 *
 * Centralises the Phase-D bug fix originally written for Fixed Assets in
 * response to the Freelook "Head Office data everywhere" incident.  The
 * underlying defect was a runs list rendering ``runs.map(...)`` without
 * filtering by the URL's scope — so a run created at Head Office scope
 * was visible from Dyeing / Windmill / Solar URLs too.
 *
 * Same defect existed in latent form on Balance Confirmation and MSME
 * Landings; this hook gives every module the exact same behaviour:
 *
 *   • Runs whose ``scope_key`` matches the URL scope are visible.
 *   • Legacy runs without a ``scope_key`` default to ``consolidation``.
 *   • An admin "Show all" escape hatch exposes everything when needed.
 *
 * Also exposes ``isScopeMismatch(run)`` so each module can independently
 * disable destructive actions when an in-run page is loaded under a
 * URL whose scope doesn't match the run's scope.
 *
 * The hook is intentionally tiny + pure — no DOM, no useEffect, no
 * network.  Easy to unit-test (see ``__tests__/useScopedRuns.test.js``).
 */
import { useCallback, useMemo, useState } from "react";


/** Pure helper — mirrored by the Jest tests to lock the invariant. */
export function scopeFilterRuns(runs, urlScopeKey, showAll) {
  if (showAll) return runs || [];
  const want = urlScopeKey || "consolidation";
  return (runs || []).filter((r) => (r?.scope_key || "consolidation") === want);
}


/** Pure helper — mirrored by the Jest tests. */
export function isScopeMismatchHelper(run, urlScopeKey) {
  if (!run) return false;
  const urlKey = urlScopeKey || "consolidation";
  const runKey = run.scope_key || "consolidation";
  return urlKey !== runKey;
}


export default function useScopedRuns(runs, urlScope) {
  const [showAllRuns, setShowAllRuns] = useState(false);

  const scopedRuns = useMemo(
    () => scopeFilterRuns(runs, urlScope?.scopeKey, showAllRuns),
    [runs, urlScope?.scopeKey, showAllRuns],
  );

  const isScopeMismatch = useCallback(
    (run) => isScopeMismatchHelper(run, urlScope?.scopeKey),
    [urlScope?.scopeKey],
  );

  return {
    scopedRuns,
    showAllRuns,
    setShowAllRuns,
    isScopeMismatch,
    // Stable summary for banners — "showing 3 of 7 runs (filtered by scope)".
    totalRuns: (runs || []).length,
    visibleRuns: scopedRuns.length,
    hiddenByScope: Math.max(0, (runs || []).length - scopedRuns.length),
  };
}
